import React from 'react';
import { Tabs, Tab, Theme, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import pluralize from 'pluralize';
import { ActivityTabValue } from './ActivityTypes';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { ActivitiesMobileMenu } from './ActivitiesMobileMenu';
import { useOrgInfo } from '../../../global/auth/use-org-info/use-org-info';
import { getMenuLabel } from '../../../global/auth/use-menu-labels/use-menu-labels';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabRoot: {
      textTransform: 'capitalize',
      fontWeight: 800,
      fontSize: '20px',
      paddingBottom: '14px',
      minWidth: 'unset',
    },
    selected: {
      color: `${theme.color.grey.neutralBrand800} !important`,
    },
    tabsFlexContainer: {
      alignContent: 'space-between',
      borderBottom: '2px solid',
      borderBottomColor: '#EFEFEF',
      height: 'calc(100% - 2px)',
    },
    root: {
      flex: 1,
      marginLeft: 60,
      marginRight: 60,
      marginTop: '1rem',
    },
  })
);

const DualLabel = ({ name, originalName }: { name: string; originalName?: string }) => {
  return (
    <Box display='flex' flexDirection='column' alignItems='flex-start' justifyContent='flex-end' minHeight='32px'>
      <Typography
        style={{
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: '1',
          marginBottom: '2px',
          textTransform: 'capitalize',
          visibility: originalName ? 'visible' : 'hidden',
          height: '12px',
        }}
      >
        {originalName || 'placeholder'}
      </Typography>
      <Typography
        style={{
          fontSize: '20px',
          fontWeight: 800,
          textTransform: 'capitalize',
          lineHeight: '1',
        }}
      >
        {name}
      </Typography>
    </Box>
  );
};

interface Props {
  activeTab: ActivityTabValue;
  onSelectTab: (value: ActivityTabValue) => void;
  children?: React.ReactNode;
}

export const ActivitiesTabs = ({ activeTab = 'all', onSelectTab, children }: Props) => {
  const classes = useStyles();
  const { isMobile } = useCampfireTheme();
  const org = useOrgInfo();

  const handleChange = (_unused: any, newValue: ActivityTabValue) => {
    onSelectTab(newValue);
  };

  const activitiesLabel = getMenuLabel('volunteeringActivities', org?.menuLabels);
  const rostersLabel = getMenuLabel('managementRosters', org?.menuLabels);

  // Get word replacements
  const wordProgram = org?.menuLabels?.wordProgram || 'Program';
  const wordActivity = org?.menuLabels?.wordActivity || 'Activity';
  const wordRoster = org?.menuLabels?.wordRoster || 'Roster';

  // Check if word replacements are custom
  const isProgramCustom = org?.menuLabels?.wordProgram && org.menuLabels.wordProgram !== 'Program';
  const isActivityCustom = org?.menuLabels?.wordActivity && org.menuLabels.wordActivity !== 'Activity';
  const isRosterCustom = org?.menuLabels?.wordRoster && org.menuLabels.wordRoster !== 'Roster';

  return isMobile ? (
    <Box display='flex' alignItems='center' justifyContent='space-between'>
      <ActivitiesMobileMenu />
      {children}
    </Box>
  ) : (
    <Tabs
      value={activeTab}
      onChange={handleChange}
      indicatorColor='primary'
      textColor='primary'
      aria-label='icon tabs example'
      classes={{
        flexContainer: classes.tabsFlexContainer,
      }}
    >
      <Tab
        label={
          <DualLabel
            name={`All ${pluralize(wordProgram)}`}
            originalName={isProgramCustom ? `All ${pluralize('Program')}` : undefined}
          />
        }
        classes={{ selected: classes.selected, root: classes.tabRoot }}
        value='programs'
      />
      <Tab
        label={
          <DualLabel
            name={activitiesLabel.customLabel || `All ${pluralize(wordActivity)}`}
            originalName={
              activitiesLabel.customLabel
                ? activitiesLabel.label
                : isActivityCustom
                ? `All ${pluralize('Activity')}`
                : undefined
            }
          />
        }
        classes={{ selected: classes.selected, root: classes.tabRoot }}
        value='all'
      />
      <Tab
        label={
          <DualLabel
            name={rostersLabel.customLabel || `My ${pluralize(wordRoster)}`}
            originalName={
              rostersLabel.customLabel ? rostersLabel.label : isRosterCustom ? `My ${pluralize('Roster')}` : undefined
            }
          />
        }
        classes={{ selected: classes.selected, root: classes.tabRoot }}
        value='mine'
      />
      {children}
    </Tabs>
  );
};
